import { Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { getGuide, GUIDES } from "@/data/guides";
import { CLINIC, SITE_URL } from "@/data/clinic";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

export default function GuidePage({ slug }) {
    const guide = getGuide(slug);
    if (!guide) return <Navigate to="/" replace />;

    const title = `${guide.title} | Agastya Chiro, Kurnool`;
    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            mainEntityOfPage: `${SITE_URL}/${guide.slug}`,
            author: { "@type": "Organization", name: CLINIC.name, url: SITE_URL },
            publisher: { "@type": "Organization", name: CLINIC.name, url: SITE_URL },
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
                { "@type": "ListItem", position: 2, name: guide.title, item: `${SITE_URL}/${guide.slug}` },
            ],
        },
    ];

    const related = GUIDES.filter((g) => g.slug !== slug).slice(0, 3);

    return (
        <div className="bg-cream text-ink" data-testid={`guide-page-${slug}`}>
            <Seo title={title} description={guide.description} path={`/${slug}`} jsonLd={jsonLd} />
            <Header />
            <main className="mx-auto max-w-3xl px-4 pb-24 pt-36 sm:px-6 md:pt-44">
                <Reveal>
                    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-moss">
                        <Link to="/" className="transition-colors duration-300 hover:text-forest" data-testid="breadcrumb-home">
                            Home
                        </Link>
                        <span aria-hidden="true">/</span>
                        <span className="text-terracotta">Guide</span>
                    </nav>
                    <h1
                        className="mt-6 font-serif text-4xl font-medium leading-[1.08] tracking-tight md:text-5xl"
                        data-testid="guide-title"
                    >
                        {guide.title}
                    </h1>
                    <p className="mt-6 border-l-2 border-gold pl-5 font-serif text-lg italic leading-relaxed text-moss md:text-xl">
                        {guide.intro}
                    </p>
                </Reveal>

                <article className="mt-12 space-y-12">
                    {guide.sections.map((s, i) => (
                        <Reveal key={i} delay={0.05}>
                            <section>
                                <h2 className="font-serif text-2xl font-medium text-forest md:text-3xl">
                                    {s.h}
                                </h2>
                                {s.p.map((para, j) => (
                                    <p key={j} className="mt-4 text-sm leading-relaxed text-moss md:text-base">
                                        {para}
                                    </p>
                                ))}
                            </section>
                        </Reveal>
                    ))}
                </article>

                <Reveal>
                    <div className="mt-16 rounded-3xl bg-forest p-8 text-cream md:p-10" data-testid="guide-cta">
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                            Experience it in Kurnool
                        </p>
                        <h2 className="mt-3 font-serif text-2xl font-medium md:text-3xl">
                            Visit {CLINIC.shortName}
                        </h2>
                        <p className="mt-3 flex items-start gap-2 text-sm leading-relaxed text-cream/80">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                            {CLINIC.addressLines.join(" ")}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <a
                                href={CLINIC.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="guide-book-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3 text-sm font-semibold text-ink transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold"
                            >
                                Book a Consultation
                            </a>
                            <a
                                href={CLINIC.phoneTel}
                                data-testid="guide-call-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-cream/50 px-6 py-3 text-sm font-semibold text-cream transition-colors duration-300 hover:border-cream"
                            >
                                <Phone className="h-4 w-4" /> Call {CLINIC.phoneDisplay}
                            </a>
                        </div>
                    </div>
                </Reveal>

                <Reveal>
                    <p className="mt-10 rounded-2xl border border-sand bg-bone p-5 text-xs leading-relaxed text-moss">
                        This guide is educational. Marma Chikitsa is a traditional, complementary
                        wellness practice — it does not diagnose, treat, cure or prevent any disease.
                        Please consult a qualified physician for any health concern. Read our full{" "}
                        <Link to="/medical-disclaimer" className="font-semibold text-forest underline underline-offset-2">
                            medical disclaimer
                        </Link>
                        .
                    </p>
                </Reveal>

                <Reveal>
                    <div className="mt-14 border-t border-sand pt-10">
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">
                            Keep reading
                        </p>
                        <ul className="mt-5 space-y-3">
                            {related.map((r) => (
                                <li key={r.slug}>
                                    <Link
                                        to={`/${r.slug}`}
                                        data-testid={`related-${r.slug}`}
                                        className="group inline-flex items-center gap-2 font-serif text-xl text-ink transition-colors duration-300 hover:text-forest"
                                    >
                                        {r.title}
                                        <ArrowRight className="h-4 w-4 text-terracotta transition-transform duration-300 group-hover:translate-x-1" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>
            </main>
            <Footer />
        </div>
    );
}
